// See the current list of valid CCP product IDS here:
import type { SerializablePayload, SerializableRecord } from './serializable-record';

// Unions must stay direct string literals: Vindicated silently drops checks for `typeof <const>`
// and `Map[keyof Map]`. Covers<> keeps each union and its const object in sync.
type Covers<Union extends string, Actual extends string> =
	Exclude<Union, Actual> extends never ? true : never;

// https://hello.atlassian.net/wiki/spaces/ECON/pages/2339030895/App+Identifiers
export type ValidProductCcpId =
	| 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f'
	| 'de63a248-1f93-46ed-b6ea-ab8e38af1c88'
	| '46ccaa9e-cdb0-4995-8886-5827a5ff162b'
	| 'a6f908c8-709c-45f4-9e2d-eca25b93b94a'
	| '6aab3f4e-cc71-474d-8d28-abef8b057808'
	| '0b8ea1e2-52df-11ea-8d77-2e728ce88125'
	| '7c917705-866d-4b6b-b266-0fc7633e8342'
	| '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2'
	| 'f44da23e-5907-4d81-8a78-33bedee10969'
	| 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3'
	| 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551'
	| '91ae3e3f-138e-43be-98cc-48df03d13c32'
	| '56cce7cb-8c54-4103-8736-e64d1ab00128'
	| '7683a73e-423d-41a1-b43f-d497656f8b5d'
	| 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905'
	| 'dbe4d391-d570-4313-ab0f-3a02dbab4720'
	| 'any'
	| 'fake-id';

export const ValidCcpId: {
	readonly JiraSoftwareCcpId: 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f';
	readonly JiraServiceManagementCcpId: 'de63a248-1f93-46ed-b6ea-ab8e38af1c88';
	readonly JiraWorkManagementCloudCcpId: '46ccaa9e-cdb0-4995-8886-5827a5ff162b';
	readonly JiraProductDiscoveryCcpId: 'a6f908c8-709c-45f4-9e2d-eca25b93b94a';
	readonly ConfluenceCloudCcpId: '6aab3f4e-cc71-474d-8d28-abef8b057808';
	readonly StatuspageCcpId: '0b8ea1e2-52df-11ea-8d77-2e728ce88125';
	readonly AtlasCcpId: '7c917705-866d-4b6b-b266-0fc7633e8342';
	readonly CompassCcpId: '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2';
	readonly AtlassianAnalyticsCcpId: 'f44da23e-5907-4d81-8a78-33bedee10969';
	readonly AtlassianWhiteboardCcpId: 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3';
	readonly OpsgenieCcpId: 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551';
	readonly RovoCcpId: '91ae3e3f-138e-43be-98cc-48df03d13c32';
	readonly RovoDevCcpId: '56cce7cb-8c54-4103-8736-e64d1ab00128';
	readonly LoomCcpId: '7683a73e-423d-41a1-b43f-d497656f8b5d';
	readonly FocusCcpId: 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905';
	readonly StudioCcpId: 'dbe4d391-d570-4313-ab0f-3a02dbab4720';
	readonly AnyProductCcpId: 'any';
	readonly TestProductCcpId: 'fake-id';
} = {
	JiraSoftwareCcpId: 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f',
	JiraServiceManagementCcpId: 'de63a248-1f93-46ed-b6ea-ab8e38af1c88',
	JiraWorkManagementCloudCcpId: '46ccaa9e-cdb0-4995-8886-5827a5ff162b',
	JiraProductDiscoveryCcpId: 'a6f908c8-709c-45f4-9e2d-eca25b93b94a',
	ConfluenceCloudCcpId: '6aab3f4e-cc71-474d-8d28-abef8b057808',
	StatuspageCcpId: '0b8ea1e2-52df-11ea-8d77-2e728ce88125',
	AtlasCcpId: '7c917705-866d-4b6b-b266-0fc7633e8342',
	CompassCcpId: '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2',
	AtlassianAnalyticsCcpId: 'f44da23e-5907-4d81-8a78-33bedee10969',
	AtlassianWhiteboardCcpId: 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3',
	OpsgenieCcpId: 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551',
	RovoCcpId: '91ae3e3f-138e-43be-98cc-48df03d13c32',
	RovoDevCcpId: '56cce7cb-8c54-4103-8736-e64d1ab00128',
	LoomCcpId: '7683a73e-423d-41a1-b43f-d497656f8b5d',
	FocusCcpId: 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905',
	StudioCcpId: 'dbe4d391-d570-4313-ab0f-3a02dbab4720',
	AnyProductCcpId: 'any',
	TestProductCcpId: 'fake-id',
};

true satisfies Covers<ValidProductCcpId, (typeof ValidCcpId)[keyof typeof ValidCcpId]>;

export const JiraSoftwareCcpId: ValidProductCcpId = ValidCcpId.JiraSoftwareCcpId;
export const JiraServiceManagementCcpId: ValidProductCcpId = ValidCcpId.JiraServiceManagementCcpId;
export const JiraWorkManagementCloudCcpId: ValidProductCcpId =
	ValidCcpId.JiraWorkManagementCloudCcpId;
export const JiraProductDiscoveryCcpId: ValidProductCcpId = ValidCcpId.JiraProductDiscoveryCcpId;
export const ConfluenceCloudCcpId: ValidProductCcpId = ValidCcpId.ConfluenceCloudCcpId;
export const StatuspageCcpId: ValidProductCcpId = ValidCcpId.StatuspageCcpId;
export const AtlasCcpId: ValidProductCcpId = ValidCcpId.AtlasCcpId;
export const CompassCcpId: ValidProductCcpId = ValidCcpId.CompassCcpId;
export const AtlassianAnalyticsCcpId: ValidProductCcpId = ValidCcpId.AtlassianAnalyticsCcpId;
export const AtlassianWhiteboardCcpId: ValidProductCcpId = ValidCcpId.AtlassianWhiteboardCcpId;
export const OpsgenieCcpId: ValidProductCcpId = ValidCcpId.OpsgenieCcpId;
export const RovoCcpId: ValidProductCcpId = ValidCcpId.RovoCcpId;
export const RovoDevCcpId: ValidProductCcpId = ValidCcpId.RovoDevCcpId;
export const LoomCcpId: ValidProductCcpId = ValidCcpId.LoomCcpId;
export const FocusCcpId: ValidProductCcpId = ValidCcpId.FocusCcpId;
export const StudioCcpId: ValidProductCcpId = ValidCcpId.StudioCcpId;
export const AnyProductCcpId: ValidProductCcpId = ValidCcpId.AnyProductCcpId;
export const TestProductCcpId: ValidProductCcpId = ValidCcpId.TestProductCcpId;

/**
 * productId: This needs to be added for app-switcher message templates.
 *            It should be the product related to the message itself.
 *            This ID is used by the recommendations service.
 */
export type MessageMetadata = SerializableRecord & {
	productId?: ValidProductCcpId;
};

export type TransformMetadataResult = {
	rerankingMetadata?: Record<string, string | number | boolean>;
	goalMetadata: GoalMetadata;
	[K: string]: SerializablePayload;
};

export type ValidProductEdition = 'free' | 'standard' | 'premium' | 'any';

export const ValidEdition: {
	readonly FreeEdition: 'free';
	readonly StandardEdition: 'standard';
	readonly PremiumEdiiton: 'premium';
	readonly AnyEdition: 'any';
} = {
	FreeEdition: 'free',
	StandardEdition: 'standard',
	PremiumEdiiton: 'premium',
	AnyEdition: 'any',
};

true satisfies Covers<ValidProductEdition, (typeof ValidEdition)[keyof typeof ValidEdition]>;

export const FreeEdition: ValidProductEdition = ValidEdition.FreeEdition;
export const StandardEdition: ValidProductEdition = ValidEdition.StandardEdition;
export const PremiumEdiiton: ValidProductEdition = ValidEdition.PremiumEdiiton;
export const AnyEdition: ValidProductEdition = ValidEdition.AnyEdition;

export type ValidTargetProductGoalId =
	| 'signup'
	| 'd1to6ai'
	| 'ffp'
	| 'fo'
	| 'mcu'
	| 'mau'
	| 'purchase'
	| 'm2_ati'
	| 'peu';

export const ValidTargetProductGoalIdentifiers: {
	readonly SignupGoal: 'signup';
	readonly D1to6aiGoal: 'd1to6ai';
	readonly FfpGoal: 'ffp';
	readonly FoGoal: 'fo';
	readonly McuGoal: 'mcu';
	readonly MauGoal: 'mau';
	readonly PurchaseGoal: 'purchase';
	readonly M2AtiGoal: 'm2_ati';
	readonly PeuGoal: 'peu';
} = {
	SignupGoal: 'signup',
	D1to6aiGoal: 'd1to6ai',
	FfpGoal: 'ffp',
	FoGoal: 'fo',
	McuGoal: 'mcu',
	MauGoal: 'mau',
	PurchaseGoal: 'purchase',
	M2AtiGoal: 'm2_ati',
	PeuGoal: 'peu',
};

true satisfies Covers<
	ValidTargetProductGoalId,
	(typeof ValidTargetProductGoalIdentifiers)[keyof typeof ValidTargetProductGoalIdentifiers]
>;

export const SignupGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.SignupGoal;
export const D1to6aiGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.D1to6aiGoal;
export const FfpGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.FfpGoal;
export const FoGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.FoGoal;
export const McuGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.McuGoal;
export const MauGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.MauGoal;
export const PurchaseGoal: ValidTargetProductGoalId =
	ValidTargetProductGoalIdentifiers.PurchaseGoal;
export const M2AtiGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.M2AtiGoal;
export const PeuGoal: ValidTargetProductGoalId = ValidTargetProductGoalIdentifiers.PeuGoal;

export type ValidBasicGoalId =
	| 'event_registration'
	| 'tech_alert'
	| 'commerce_notification'
	| 'webinar_invites'
	| 'demo'
	| 'survey_research'
	| 'clickthrough'
	| 'macm';

export const ValidBasicGoalIdentifiers: {
	readonly EventRegistrationGoal: 'event_registration';
	readonly TechAlertGoal: 'tech_alert';
	readonly CommerceNotificationGoal: 'commerce_notification';
	readonly WebinarInvitesGoal: 'webinar_invites';
	readonly DemoGoal: 'demo';
	readonly SurveryResearchGoal: 'survey_research';
	readonly ClickthroughGoal: 'clickthrough';
	readonly MacmGoal: 'macm';
} = {
	EventRegistrationGoal: 'event_registration',
	TechAlertGoal: 'tech_alert',
	CommerceNotificationGoal: 'commerce_notification',
	WebinarInvitesGoal: 'webinar_invites',
	DemoGoal: 'demo',
	SurveryResearchGoal: 'survey_research',
	ClickthroughGoal: 'clickthrough',
	MacmGoal: 'macm', // Month Active Community Member
};

true satisfies Covers<
	ValidBasicGoalId,
	(typeof ValidBasicGoalIdentifiers)[keyof typeof ValidBasicGoalIdentifiers]
>;

export const EventRegistrationGoal: ValidBasicGoalId =
	ValidBasicGoalIdentifiers.EventRegistrationGoal;
export const TechAlertGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.TechAlertGoal;
export const CommerceNotificationGoal: ValidBasicGoalId =
	ValidBasicGoalIdentifiers.CommerceNotificationGoal;
export const WebinarInvitesGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.WebinarInvitesGoal;
export const DemoGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.DemoGoal;
export const SurveryResearchGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.SurveryResearchGoal;
export const ClickthroughGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.ClickthroughGoal;
export const MacmGoal: ValidBasicGoalId = ValidBasicGoalIdentifiers.MacmGoal;

export type ValidGoalId = ValidTargetProductGoalId | ValidBasicGoalId;

export const ValidGoalIdentifiers: {
	readonly SignupGoal: 'signup';
	readonly D1to6aiGoal: 'd1to6ai';
	readonly FfpGoal: 'ffp';
	readonly FoGoal: 'fo';
	readonly McuGoal: 'mcu';
	readonly MauGoal: 'mau';
	readonly PurchaseGoal: 'purchase';
	readonly M2AtiGoal: 'm2_ati';
	readonly PeuGoal: 'peu';
	readonly EventRegistrationGoal: 'event_registration';
	readonly TechAlertGoal: 'tech_alert';
	readonly CommerceNotificationGoal: 'commerce_notification';
	readonly WebinarInvitesGoal: 'webinar_invites';
	readonly DemoGoal: 'demo';
	readonly SurveryResearchGoal: 'survey_research';
	readonly ClickthroughGoal: 'clickthrough';
	readonly MacmGoal: 'macm';
} = {
	SignupGoal: 'signup',
	D1to6aiGoal: 'd1to6ai',
	FfpGoal: 'ffp',
	FoGoal: 'fo',
	McuGoal: 'mcu',
	MauGoal: 'mau',
	PurchaseGoal: 'purchase',
	M2AtiGoal: 'm2_ati',
	PeuGoal: 'peu',
	EventRegistrationGoal: 'event_registration',
	TechAlertGoal: 'tech_alert',
	CommerceNotificationGoal: 'commerce_notification',
	WebinarInvitesGoal: 'webinar_invites',
	DemoGoal: 'demo',
	SurveryResearchGoal: 'survey_research',
	ClickthroughGoal: 'clickthrough',
	MacmGoal: 'macm',
};

true satisfies Covers<
	ValidGoalId,
	(typeof ValidGoalIdentifiers)[keyof typeof ValidGoalIdentifiers]
>;

/**
 * The device details object allows for filtering on a
 * user's device. To read more check out the socrates docs:
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#track--ui---operational
 */
export type DeviceDetails = {
	deviceType: string;
	deviceBrand: string;
	device: string;
	deviceFamily: string;
};

/**
 * Post Office currently only supports GASv3 analytics events as a source
 * The data field is where message creators are expected to enter information
 * about the GASv3 they wish to use as their message instance's success metric
 */
export type MessageGoalMetadata = {
	source: 'gasv3';
	data: MessageGoalMetadataGasV3;
};

// We are currently only supporting these event types
// We are explicitly excluding 'operational' events to help
// guiude message creators towards using in-product analytics events
export const UIEventTypeGASv3 = 'ui';
export const TrackEventTypeGASv3 = 'track';
export const ScreenEventTypeGASv3 = 'screen';

const ValidGASv3EventTypes: {
	readonly UIEventTypeGASv3: 'ui';
	readonly TrackEventTypeGASv3: 'track';
	readonly ScreenEventTypeGASv3: 'screen';
} = {
	UIEventTypeGASv3: UIEventTypeGASv3,
	TrackEventTypeGASv3: TrackEventTypeGASv3,
	ScreenEventTypeGASv3: ScreenEventTypeGASv3,
} as const;

/**
 * The analytic event types that Post Office is supporting
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#introduction
 */
export type MessageGoalGasV3EventTypes = 'ui' | 'track' | 'screen';

true satisfies Covers<
	MessageGoalGasV3EventTypes,
	(typeof ValidGASv3EventTypes)[keyof typeof ValidGASv3EventTypes]
>;

/**
 * The structure of screen GASv3 events differs slightly from the UI and track event types
 * For more information:
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#introduction
 */
export type MessageGoalMetadataGasV3 =
	| {
			eventType: 'screen';
			eventName: string;
			product: string;
			eventAttributes?: Record<string, string>;
			deviceDetails?: DeviceDetails;
	  }
	| {
			eventType: 'ui' | 'track';
			action: string;
			actionSubject: string;
			eventName: string;
			product: string;
			eventAttributes?: Record<string, string>;
			deviceDetails?: DeviceDetails;
	  };

// BasicGoals are goals that do not have any additional required fields.
export type BasicGoal = {
	goalId: ValidBasicGoalId;
};

// TargetProductGoals are goals that are required to have the targetProduct and edition fields.
export type TargetProductGoal = {
	goalId: ValidTargetProductGoalId;
	targetProduct: ValidProductCcpId[];
	edition: ValidProductEdition[];
};

export type BusinessGoalMetadata = TargetProductGoal | BasicGoal;

export type GoalMetadata = {
	primaryGoal: BusinessGoalMetadata;
	secondaryGoal?: BusinessGoalMetadata;
	successMetric?: MessageGoalMetadata[];
};
